// ===========================================
// Charm Promo Enforcement (Pierre + Welcome Bracelet) — optimized + quiet
// + RELIABLE qty UI hide/lock for promo line items
//
// What this does:
// - Auto-add promo item only on approved landing hash/path (same as your current logic)
// - Enforce: remove promo item if required code missing
// - Enforce: promo qty must be 1 (server-side via /cart/change.js)
// - Enforce: hide/disable qty controls in SlideCart + /cart page
// - Still "quiet": no global cart listeners; observer is ONLY attached
//   when promo is in cart (or promo URL context), and scoped to SlideCart root.
// ===========================================

(() => {
  // --------------------
  // Guard against double-loading
  // --------------------
  if (window.__CHARM_PROMO_ENFORCE_INIT__) return;
  window.__CHARM_PROMO_ENFORCE_INIT__ = true;

  // --------------------
  // Config: list of charm promos to enforce
  // --------------------
  const CHARM_PROMOS = [
    { variantId: "46790830882982", requiredCode: "CHARMED2MEETU" },
    { variantId: "46715440890022", requiredCode: "CHARMEDTOMEETYOU" },
  ];

  const PROMO_BY_VARIANT = new Map(CHARM_PROMOS.map((p) => [String(p.variantId), p]));
  const PROMO_VARIANT_ID_SET = new Set(CHARM_PROMOS.map((p) => String(p.variantId)));

  // --------------------
  // Landing config for auto-adding promo item
  // --------------------
  const CHARM_LANDING = {
    variantId: "46715440890022",
    requiredCode: "CHARMEDTOMEETYOU",
    targetPaths: [
      "/collections/bestsellers",
      "/collections/craft-kits-for-beginners",
      "/collections/all-beginner-and-plus-crochet-kits",
      "/collections/collab",
      "/collections/bundles",
      "/collections/collectors-boxes",
      "/collections/bestsellers/products/penguin-crochet-kit",
      "/collections/bestsellers/products/dinosaur-crochet-kit",
      "/collections/bestsellers/products/easy-peasy-beginner-bundle",
      "/collections/bestsellers/products/fox-crochet-kit",
      "/collections/new-arrivals",
      "/collections/woobly-wonderland",
    ],
    hash: "#promo_charm_bestsellers",
    sessionFlag: "promo_charm_bestsellers_applied",
  };

  let firstCharmEnforcement = true;

  // --------------------
  // Cart fetch helper (in-flight de-dupe + tiny cache)
  // --------------------
  let _cartInflight = null;
  let _cartCache = null;
  let _cartCacheAt = 0;
  const CART_CACHE_MS = 350;

  async function getCart({ bust = false } = {}) {
    const now = Date.now();
    if (!bust && _cartCache && now - _cartCacheAt < CART_CACHE_MS) return _cartCache;
    if (_cartInflight) return _cartInflight;

    _cartInflight = fetch("/cart.js", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((cart) => {
        _cartCache = cart;
        _cartCacheAt = Date.now();
        return cart;
      })
      .finally(() => {
        _cartInflight = null;
      });

    return _cartInflight;
  }

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  async function getCartWithOptionalWait(retries = 5, delay = 250) {
    const initialCart = await getCart();
    const hasPromoItem = (initialCart.items || []).some((item) =>
      PROMO_VARIANT_ID_SET.has(String(item.variant_id))
    );

    if (!hasPromoItem) return initialCart;

    for (let i = 0; i < retries; i++) {
      const cart = await getCart({ bust: true });
      const hasCode = Array.isArray(cart.discount_codes) && cart.discount_codes.length > 0;
      if (hasCode) return cart;
      await sleep(delay);
    }

    return getCart({ bust: true });
  }

  // --------------------
  // Landing helpers
  // --------------------
  function isOnCharmLandingPath() {
    try {
      const currentPath = (window.location.pathname || "").replace(/\/+$/, "");
      return CHARM_LANDING.targetPaths.some((rawPath) => {
        const base = (rawPath || "").replace(/\/+$/, "");
        return currentPath === base || currentPath.startsWith(base + "/");
      });
    } catch (e) {
      console.warn("[CharmPromo] Unable to evaluate landing path", e);
      return false;
    }
  }

  function cleanCharmPromoHash() {
    try {
      if (window.location.hash === CHARM_LANDING.hash && window.history?.replaceState) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    } catch (e) {
      console.warn("[CharmPromo] Unable to clean hash", e);
    }
  }

  async function maybeAutoAddCharmPromoFromLanding() {
    try {
      if (!isOnCharmLandingPath()) return;
      if (window.location.hash !== CHARM_LANDING.hash) return;

      if (sessionStorage.getItem(CHARM_LANDING.sessionFlag)) {
        cleanCharmPromoHash();
        return;
      }

      const cart = await getCart();
      const alreadyInCart = (cart.items || []).some(
        (item) => String(item.variant_id) === String(CHARM_LANDING.variantId)
      );

      if (alreadyInCart) {
        sessionStorage.setItem(CHARM_LANDING.sessionFlag, "true");
        cleanCharmPromoHash();
        return;
      }

      const addRes = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ items: [{ id: CHARM_LANDING.variantId, quantity: 1 }] }),
      });

      if (!addRes.ok) {
        console.warn("[CharmPromo] /cart/add.js returned non-OK", addRes.status);
      }

      _cartCache = null;
      sessionStorage.setItem(CHARM_LANDING.sessionFlag, "true");
      cleanCharmPromoHash();

      window.SLIDECART_UPDATE?.();
    } catch (err) {
      console.error("[CharmPromo] Error auto-adding charm promo", err);
    }
  }

  // -------------------------------------------
  // URL-gated trigger (quiet)
  // -------------------------------------------
  function isCharmPromoContext() {
    // Landing flow (hash + allowed path)
    if (isOnCharmLandingPath() && window.location.hash === CHARM_LANDING.hash) return true;

    // Session says we already processed the landing add this session
    if (sessionStorage.getItem(CHARM_LANDING.sessionFlag)) return true;

    // If user is on /discount/CHARMED... URL, treat as promo context
    const m = window.location.pathname.match(/^\/discount\/([^\/]+)/);
    if (m && m[1] && decodeURIComponent(m[1]).toUpperCase().includes("CHARMED")) return true;

    // If query has discount=CHARMED...
    if (typeof getParam === "function") {
      const d = (getParam("discount") || getParam("auto-discount") || "").toUpperCase();
      if (d.includes("CHARMED")) return true;
    }

    return false;
  }

  // --------------------
  // UI Hide/Lock (SlideCart + theme cart qty controls)
  // --------------------
  function hideAndDisableQtyUIForVariant(variantId) {
    const vid = String(variantId);

    // Prefer SlideCart root if present; fall back to document
    const root =
      document.querySelector("#slidecarthq") ||
      document.querySelector(".slidecarthq") ||
      document;

    // More forgiving selector: different carts use different attrs
    const lines = root.querySelectorAll(
      `[data-variant-id="${vid}"], [data-variant="${vid}"], [data-variantid="${vid}"]`
    );

    lines.forEach((line) => {
      // Try to hide a whole qty wrapper first (best UX)
      const wrapper =
        line.querySelector(".quantity-selector") ||
        line.querySelector(".cart-item__quantity") ||
        line.querySelector(".line-item__quantity");

      const dec = line.querySelector('button[aria-label="decrease quantity"]');
      const inc = line.querySelector('button[aria-label="increase quantity"]');
      const input = line.querySelector(
        ".quantity-selector input[type='text'], .quantity-selector input[type='number']"
      );

      // Disable + hide individual controls (safe even if wrapper found)
      if (dec) {
        dec.disabled = true;
        dec.setAttribute("aria-disabled", "true");
        dec.style.display = "none";
        dec.classList.add("charm-promo-qty-hidden");
      }
      if (inc) {
        inc.disabled = true;
        inc.setAttribute("aria-disabled", "true");
        inc.style.display = "none";
        inc.classList.add("charm-promo-qty-hidden");
      }
      if (input) {
        input.readOnly = true;
        input.value = "1";
        input.style.display = "none";
        input.classList.add("charm-promo-qty-hidden");
      }

      if (wrapper) {
        wrapper.style.display = "none";
        wrapper.classList.add("charm-promo-qty-hidden");
      }
    });

    // /cart page (Dawn-ish)
    document.querySelectorAll(".cart__qty-input").forEach((input) => {
      const match = input.id && input.id.match(/updates_large_(\d+)/);
      if (match && match[1] === vid) {
        input.readOnly = true;
        input.value = "1";
        input.style.display = "none";
        input.classList.add("charm-promo-qty-hidden");

        const wrap =
          input.closest(".cart__qty") ||
          input.closest(".quantity") ||
          input.closest(".quantity-selector");
        if (wrap) {
          wrap.style.display = "none";
          wrap.classList.add("charm-promo-qty-hidden");
        }
      }
    });
  }

  // --------------------
  // Scoped observer: ONLY when promo exists (or promo context)
  // --------------------
  let _uiObserver = null;
  let _uiObserverAttached = false;

  function attachPromoUiObserver() {
    if (_uiObserverAttached) return;

    const target =
      document.querySelector("#slidecarthq") ||
      document.querySelector(".slidecarthq");

    if (!target) return;

    _uiObserverAttached = true;

    // Keep it light: just re-apply hides when SlideCart rerenders
    _uiObserver = new MutationObserver(() => {
      PROMO_VARIANT_ID_SET.forEach((vid) => hideAndDisableQtyUIForVariant(vid));
    });

    _uiObserver.observe(target, { childList: true, subtree: true });

    // Apply immediately too
    PROMO_VARIANT_ID_SET.forEach((vid) => hideAndDisableQtyUIForVariant(vid));
  }

  // If SlideCart mounts later, try a few times (no global long-lived observer)
  async function tryAttachObserverSoon(tries = 10, delay = 300) {
    for (let i = 0; i < tries; i++) {
      if (_uiObserverAttached) return;
      attachPromoUiObserver();
      if (_uiObserverAttached) return;
      await sleep(delay);
    }
  }

  // --------------------
  // Enforcement logic (server + UI)
  // --------------------
  let _enforceInflight = false;
  let _enforceQueued = false;

  async function enforcePromoRules({ longWait = false } = {}) {
    if (_enforceInflight) {
      _enforceQueued = true;
      return;
    }
    _enforceInflight = true;
    _enforceQueued = false;

    try {
      const retries = longWait || firstCharmEnforcement ? 16 : 5;
      const delay = longWait || firstCharmEnforcement ? 400 : 250;

      const cart = await getCartWithOptionalWait(retries, delay);
      firstCharmEnforcement = false;
      if (!cart || !Array.isArray(cart.items)) return;

      const promoItems = cart.items.filter((item) =>
        PROMO_VARIANT_ID_SET.has(String(item.variant_id))
      );

      if (promoItems.length === 0) return;

      // Promo exists -> ensure observer attaches so UI stays hidden through rerenders
      tryAttachObserverSoon(8, 250);

      const activeCodes = new Set(
        (cart.discount_codes || []).map((dc) => (dc.code || "").toUpperCase())
      );

      for (const item of promoItems) {
        const variantId = String(item.variant_id);
        const promo = PROMO_BY_VARIANT.get(variantId);
        if (!promo) continue;

        const requiredCode = promo.requiredCode.toUpperCase();
        const hasRequiredCode = activeCodes.has(requiredCode);

        // Remove promo line if required code missing
        if (!hasRequiredCode) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          });
          _cartCache = null;
          window.SLIDECART_UPDATE?.();
          continue;
        }

        // Force qty=1 (server-side)
        if (item.quantity !== 1) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ id: item.key, quantity: 1 }),
          });
          _cartCache = null;
          window.SLIDECART_UPDATE?.();
        }

        // Hide qty UI (client-side)
        hideAndDisableQtyUIForVariant(variantId);
      }
    } finally {
      _enforceInflight = false;
      if (_enforceQueued) {
        _enforceQueued = false;
        // quick follow-up to catch render timing
        setTimeout(() => enforcePromoRules({ longWait: false }).catch(() => {}), 200);
      }
    }
  }

  // --------------------
  // Init
  // --------------------
  document.addEventListener("DOMContentLoaded", async () => {
    if (!isCharmPromoContext()) return;

    console.log("[CharmPromo] Promo URL context detected — auto-add + enforce");

    // Attach observer early if we're in promo context (SlideCart may render later)
    tryAttachObserverSoon(8, 250);

    // Try auto-add (no-op unless hash/path match)
    await maybeAutoAddCharmPromoFromLanding();

    // One enforcement run (long wait on first load)
    await enforcePromoRules({ longWait: true });

    // Short follow-up to catch cart + DOM settling
    setTimeout(() => {
      enforcePromoRules({ longWait: false }).catch(() => {});
    }, 350);
  });
})();